import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import useDeviceDetect from 'Hooks/useDeviceDetect';
import { useOptionalMessage } from 'Hooks/useOptionalMessage';
import { WizardStepHeader } from '../Shared/WizardStepHeader';
import useFetchBookingPriceBreakdown from 'Hooks/useFetchBookingPrice';
import { FreightCategoryEntity, FreightHandlingEntity } from 'Models/Entities';
import { FreightTypeDto } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { IPriceLineItem } from 'Services/Api/_HumanWritten/PricingService/PricingResult';
import If from 'Views/Components/If/If';
import { BookingWizardData, getDefaultInnerBookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { FreightBookingWizardData } from '../../BookingWizardData/FreightBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { useWizardFetchedDataStore } from '../../BookingWizardState/BookingWizardFetchedData';
import { WizardErrors } from '../../BookingWizardTypes';
import { ShipmentCalculationSection } from './ShipmentCalculationSection';
import { ShipmentDescriptionSection } from './ShipmentDescriptionSection';
import { appConfigStore } from 'Models/AppConfigStore';

function ShipmentTab() {
	const intl = useIntl();
	const fmtOptional = useOptionalMessage();
	const { isIpad } = useDeviceDetect();
	const [freightTypeOptions, setFreightTypeOptions] = useState<{ [key in string]: FreightTypeDto[] }>({});
	const [freightCategoryOptions, setFreightCategoryOptions] = useState<FreightCategoryEntity[]>([]);
	const [handlingOptions, setHandlingOptions] = useState<FreightHandlingEntity[]>([]);

	const [freightPriceLineItem, setFreightPriceLineItem] = useState<IPriceLineItem | undefined>(undefined);
	const [triggerCalculation, setTriggerCalculation] = useState<number>(1);
	const [typedErrors, setTypedErrors] = useState<WizardErrors<FreightBookingWizardData>>({});

	const { wizardData, setWizardData, innerWizardErrors, setInnerWizardErrors } = useBookingWizardDataStore();

	const { freightTabData, fetchFreightTabData } = useWizardFetchedDataStore(x => ({
		freightTabData: x.freightTabData,
		fetchFreightTabData: x.fetchFreightTabData,
	}));

	const showDimensions = useMemo(() => {
		if (wizardData.innerWizardData.type !== 'freight') {
			return false;
		}

		if (appConfigStore.freightDimensionsHiddenOnStaticPriceEnabled) {
			const { freightTypeId } = wizardData.innerWizardData;
			const freightTypes = Object.values(freightTypeOptions ?? {}).flat();
			const match = freightTypes.find(ft => ft.id === freightTypeId);
			if (match) {
				return !match.hasStaticPricing; // show dimensions when not using static price
			}
			return false;
		}

		return true;
	}, [
		appConfigStore.freightDimensionsHiddenOnStaticPriceEnabled,
		wizardData,
		wizardData.innerWizardData,
		// freightTypeOptions loads asynchronously (see the freightTabData effect below); without
		// it here the memo keeps a stale empty map on edit, so dimensions stay hidden until some
		// other wizardData change forces a recompute.
		freightTypeOptions,
	]);

	const { data: transactionInfo, refetch: refetchTransactionInfo } = useFetchBookingPriceBreakdown(
		'sidebar',
		[triggerCalculation],
		undefined,
		wizardData,
		false,
		true,
	);

	useEffect(() => {
		setTypedErrors(innerWizardErrors as WizardErrors<FreightBookingWizardData>);
	}, [innerWizardErrors]);

	useEffect(() => {
		if ((transactionInfo?.linkedBookings[0]?.departureBooking?.priceLineItems?.length ?? 0) > 0) {
			setFreightPriceLineItem(
				transactionInfo?.linkedBookings[0]?.departureBooking?.priceLineItems.find(x => x.key === 'FreightBase'),
			);
		}
	}, [transactionInfo]);

	useEffect(() => {
		if (freightTabData !== null) {
			setFreightTypeOptions(freightTabData.freightTypes ?? {});
			setFreightCategoryOptions(freightTabData.freightCategoryTypes ?? []);
			setHandlingOptions(freightTabData.freightHandlingTypes ?? []);
		} else {
			// This is a fallback in case the freight tab data isn't fetched on the initial load (but it should be)
			fetchFreightTabData();
		}
	}, [freightTabData]);

	useEffect(() => {
		const newData = new BookingWizardData({ ...wizardData });
		if (newData.innerWizardData instanceof FreightBookingWizardData) {
			setWizardData(newData);
		}
	}, [
		(wizardData.innerWizardData as FreightBookingWizardData).length,
		(wizardData.innerWizardData as FreightBookingWizardData).width,
		(wizardData.innerWizardData as FreightBookingWizardData).height,
		(wizardData.innerWizardData as FreightBookingWizardData).quantity,
		(wizardData.innerWizardData as FreightBookingWizardData).weight,
	]);

	// If there is no freight booking wizard data, we will create a new one so that the
	// booking wizard doesn't break. This should never happen, but just in case...
	if (!(wizardData.innerWizardData instanceof FreightBookingWizardData)) {
		const newData = new BookingWizardData({ ...wizardData });
		newData.innerWizardData = getDefaultInnerBookingWizardData('freight');
		setWizardData(newData);
	}

	const onAfterChange = (fieldName: keyof FreightBookingWizardData) => {
		const newData = new BookingWizardData({ ...wizardData });
		setWizardData(newData);

		const newErrors = { ...(innerWizardErrors as WizardErrors<FreightBookingWizardData>) };
		delete newErrors[fieldName];
		setInnerWizardErrors(newErrors);

		if (fieldName === 'freightTypeId' || fieldName === 'handlingIds' || fieldName === 'quantity') {
			setTriggerCalculation(old => old + 1);
		}
	};

	const freightTypeDtos = useMemo<FreightTypeDto[]>(() => {
		return Object.values(freightTypeOptions).flat();
	}, [freightTypeOptions]);

	const shipmentSubtitle = fmtOptional({
		id: 'freight-booking-wizard__shipment-details__subtitle',
		defaultMessage: "Describe the freight you're booking.",
	});

	return (
		<div className="booking-wizard-tab shipment-details-tab">
			<WizardStepHeader
				iconName="inventory_2"
				title={intl.formatMessage({
					id: 'freight-booking-wizard__shipment-details__header',
					defaultMessage: 'Enter shipment details',
				})}
				subtitle={shipmentSubtitle || undefined}
				showCloseLink={wizardData.wizardMode === 'ALTERATION'}
				bookingToEditId={wizardData?.bookingToEdit ?? ''}
			/>
			<div className="shipment-details__container flex flex-col gap-3">
				<ShipmentDescriptionSection
					typedErrors={typedErrors}
					freightTypeOptions={freightTypeOptions}
					freightCategoryOptions={freightCategoryOptions}
					freightHandlingOptions={handlingOptions}
					onAfterChange={onAfterChange}
					showDimensions={showDimensions}
				/>
				<If condition={isIpad}>
					<ShipmentCalculationSection
						freightTypeDtos={freightTypeDtos}
						priceLineItem={freightPriceLineItem}
						setPriceLineItem={setFreightPriceLineItem}
						setTriggerCalculation={setTriggerCalculation}
					/>
				</If>
			</div>
		</div>
	);
}

export default observer(ShipmentTab);
